import { ReactElement } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { toggleRow } from '../../../../store/requestFlow/actions';
import StyleAuxiliary from '../../../../hoc/Auxiliary/StyleAuxiliary/StyleAuxiliary';
import Multiselect from '../../../../components/UI/Input/Multiselect/Multiselect';
import Card from '../../../../components/UI/MDC/Card/Card';
import Badge from '@mui/material/Badge';
import {
	Headline6,
	Subtitle2,
} from '../../../../components/UI/MDC/Typography/Typography';
import { MultiselectTypes as MULTISELECT_TYPES } from '../../../../components/UI/Input/Multiselect/_types';
import { VALIDATION_THRESHOLD } from '../_constants';
import { CSS_CLASSES } from './_constants';

import classes from './RequesteeSelection.module.scss';

/**
 * Functional component for displaying, managing requestee selections from Redux in sticky multiselect.
 *
 * @param {Object} props JSX attributes passed to component.
 * @returns {ReactElement} rendered component.
 */
const RequesteeSelections = ({ sticky = true, ...props }) => {
	const dispatch = useDispatch();
	const rRequestees = useSelector(state => state.requestFlow.requestees);
	const filteredRequestees = props.filter
		? rRequestees.filter(props.filter)
		: rRequestees;

	const body = (
		<>
			<StyleAuxiliary
				classes={classnames({
					[`${classes[
						CSS_CLASSES
							.SELECTION_MULTISELECT_CONTAINER_SHORT_TITLE
					]
						}`]:
						filteredRequestees.filter(
							requestee => requestee.isSelected === true
						).length <= 99,
					[`${classes[
						CSS_CLASSES
							.SELECTION_MULTISELECT_CONTAINER_LONG_TITLE
					]
						}`]:
						filteredRequestees.filter(
							requestee => requestee.isSelected === true
						).length > 99,
				})}
			>
				<Badge
					sx={{
						'& .MuiBadge-badge': {
							top: '5px',
							right: '8px',
						},
					}}
					badgeContent={
						filteredRequestees.filter(
							requestee => requestee.isSelected === true
						).length
					}
					color='error'
				>
					<Headline6>{props.label ?? 'Current Selections'}</Headline6>
				</Badge>
			</StyleAuxiliary>
			{filteredRequestees.filter(
				requestee => requestee.isSelected === true
			).length >= VALIDATION_THRESHOLD.MAX ? (
				<Subtitle2 className={classes[CSS_CLASSES.WARNING]}>
					A maximum of {VALIDATION_THRESHOLD.MAX} selections is
					supported.
				</Subtitle2>
			) : null}
			<Multiselect
				title={null}
				actionTitle='Selections'
				options={{
					headers: props.headers,
					values: filteredRequestees,
				}}
				settings={{
					type: MULTISELECT_TYPES.TABLE,
					chipHandler: chip => {
						let row = chip.value;
						// chip structure different from row; mocking relevant row property
						row.Key = chip.key;
						dispatch(toggleRow(row));
					},
					selectHandler: row => dispatch(toggleRow(row)),
					isReadOnly: props.isReadOnly,
				}}
			/>
		</>
	);

	return filteredRequestees && filteredRequestees.length > 0 ? (
		<StyleAuxiliary
			classes={classnames({
				[classes[
					CSS_CLASSES.STICKY_SELECTION_MULTISELECT_CONTAINER
				]]: sticky,
				[classes[CSS_CLASSES.INLINE_SELECTION_MULTISELECT_CONTAINER]]: !sticky,
			})}
		>
			{
				sticky
					? <Card xlarge>{body}</Card>
					: <StyleAuxiliary>{body}</StyleAuxiliary>
			}
		</StyleAuxiliary>
	) : null;
};

RequesteeSelections.propTypes = {
	headers: PropTypes.objectOf(PropTypes.string),
	sticky: PropTypes.bool,
};

export { RequesteeSelections };
